import { Injectable } from '@angular/core';
import { MachineApiService } from './machine-api.service';
import type { TicketMergeReadinessDetails } from '../models/tickets-merge-readiness.model';

type ApiResponse<T> = { data: T; success: boolean };

export type TicketRepoRole = 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
export type TicketRepoRef = { owner: string; repo: string; role?: TicketRepoRole };
export type TicketPreflightTarget = TicketRepoRef & {
  confidence?: number;
  reason?: string;
  source?: 'auto' | 'user-confirmed';
};
export type TicketExecutionTarget = TicketRepoRef & {
  confidence?: number;
  reason?: string;
  source?: 'auto' | 'user-confirmed' | 'explicit';
};
export type TicketProjectContext = { definition?: any; schema?: any; diagram?: any; repos?: any };

export type TicketExecutionCommandLog = {
  id: string;
  phase: string;
  command: string;
  cwd?: string;
  status: 'queued' | 'running' | 'passed' | 'failed' | 'skipped';
  exitCode?: number;
  startedAt?: number;
  completedAt?: number;
  stdout?: string;
  stderr?: string;
};

export type TicketExecutionArtifact = {
  type: 'file' | 'diff' | 'log' | 'note';
  label: string;
  path?: string;
  content?: string;
};

export type TicketExecutionDraftFile = {
  path: string;
  contentText: string;
};

export type TicketExecutionResumeWorkUnit = {
  id?: string;
  owner: string;
  repo: string;
  role?: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
  goal: string;
  reason?: string;
  recipeId?: string;
  baseBranch?: string;
  headBranch?: string;
  files?: TicketExecutionDraftFile[];
  scaffoldCommands?: string[];
  verificationCommands?: string[];
  evidence?: string[];
};

/** Per-repo skill confidence when the plan was built (lesson store snapshot at planning). */
export type PlannerRepoConfidenceRow = {
  owner: string;
  repo: string;
  skillId?: string;
  verdict: 'high' | 'med' | 'low' | 'unknown';
  scorePct: number;
  reason: string;
  positives: number;
  negatives: number;
  neutrals: number;
  computedAt?: number;
};

export type TicketExecutionResumeDraft = {
  kind: 'workspace_plan';
  sourceRunId?: string;
  sourcePhase?: string;
  generatedAt: number;
  summary?: string;
  commitMessage?: string;
  prTitle?: string;
  prBody?: string;
  evidence?: string[];
  testPlan?: string[];
  workUnits: TicketExecutionResumeWorkUnit[];
  plannerRepoConfidence?: PlannerRepoConfidenceRow[];
  latestCommands?: TicketExecutionCommandLog[];
  latestArtifacts?: TicketExecutionArtifact[];
};

export type TicketExecutionEvent = {
  seq: number;
  at: number;
  type: 'run_status' | 'phase' | 'work_unit' | 'command' | 'artifact' | 'review' | 'check' | 'browser' | 'error' | 'note';
  level?: 'info' | 'warn' | 'error';
  ticketId?: string;
  workUnitId?: string;
  owner?: string;
  repo?: string;
  phase?: string;
  title: string;
  message?: string;
  command?: string;
  status?: string;
  metadata?: any;
};

export type TicketExecutionWorkUnit = {
  id: string;
  owner: string;
  repo: string;
  role?: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
  goal: string;
  reason?: string;
  recipeId?: string;
  /** Id of the Skill the router chose for this work unit. */
  skillId?: string;
  status: 'planned' | 'executing' | 'verifying' | 'review' | 'done' | 'failed';
  baseBranch?: string;
  headBranch?: string;
  pullRequest?: { number: number; title: string; url: string };
  commands?: TicketExecutionCommandLog[];
  artifacts?: TicketExecutionArtifact[];
  filesChanged?: string[];
  evidence?: string[];
  error?: string;
  startedAt?: number;
  completedAt?: number;
};

export type TicketsExecuteInput = {
  installationId: number;
  owner: string;
  repo: string;
  baseBranch?: string;
  ticket: {
    id: string;
    ticketNumber?: number;
    title: string;
    description?: string;
    repoRole?: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
    acceptanceCriteria?: string[];
    risk?: 'low' | 'med' | 'high';
  };
};

export type TicketAgentExecutionInput = {
  projectId?: string;
  installationId: number;
  repos: TicketRepoRef[];
  target?: TicketExecutionTarget;
  baseBranch?: string;
  model?: string;
  ticket: TicketsExecuteInput['ticket'];
  projectContext?: TicketProjectContext;
};

export type TicketsExecuteResult = {
  owner: string;
  repo: string;
  base: string;
  head: string;
  autoMergeScheduled?: boolean;
  pullRequest: { number: number; title: string; url: string };
  summary?: {
    message?: string;
    evidence?: string[];
    filesChanged?: string[];
    testPlan?: string[];
  };
  workUnits?: TicketExecutionWorkUnit[];
  execution?: {
    attemptsUsed: number;
    maxAttempts: number;
    parseFailures: number;
    repairAttempts: number;
    fallbacksUsed: number;
    modelsTried: string[];
    attemptedModelsInOrder: string[];
    notes?: string[];
    verificationPassed?: boolean;
    remediationAttempts?: number;
  };
};

export type TicketsPreflightInput = {
  installationId: number;
  repos: TicketRepoRef[];
  target?: TicketPreflightTarget;
  baseBranch?: string;
};

export type TicketsPreflightResult = {
  ok: boolean;
  resolvedTarget?: { owner: string; repo: string; baseBranch: string };
  checks: Array<{ key: string; ok: boolean; message: string }>;
};

export type TicketExecutionPlanInput = {
  projectId?: string;
  installationId?: number;
  repos: TicketRepoRef[];
  target?: TicketExecutionTarget;
  ticket: TicketsExecuteInput['ticket'];
  projectContext?: TicketProjectContext;
};

export type TicketExecutionPlanResult = {
  installationId: number | null;
  ticket: TicketsExecuteInput['ticket'];
  target: {
    owner: string;
    repo: string;
    role?: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
    confidence: number;
    reason: string;
    source: 'auto' | 'user-confirmed' | 'explicit';
  } | null;
  choices: Array<{ owner: string; repo: string; role: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown' }>;
  confidence: number;
  reason: string;
  ambiguous: boolean;
  needsConfirmation: boolean;
  canAutoExecute: boolean;
  issues: string[];
};

export type TicketsBuildRunStatus = 'queued' | 'planning' | 'running' | 'awaiting_input' | 'completed' | 'cancelled' | 'failed' | 'partial';
export type TicketsBuildRunItemStatus =
  | 'queued'
  | 'planning'
  | 'executing'
  | 'awaiting_confirmation'
  | 'review'
  | 'done'
  | 'failed'
  | 'manual_required'
  | 'cancelled'
  | 'skipped';

export type TicketsRunPendingDecision = {
  ticketId: string;
  title: string;
  prompt: string;
  reasonCode: string;
  suggestedAction?: 'continue';
  allowContinue?: boolean;
  allowSkip?: boolean;
  allowCancel?: boolean;
  proposedFileCount?: number;
};

export type TicketsBuildRun = {
  id: string;
  kind?: 'build' | 'ticket';
  resumedFromRunId?: string;
  projectId: string;
  status: TicketsBuildRunStatus;
  model?: string;
  startedAt?: number;
  completedAt?: number;
  currentTicketId?: string;
  currentTicketTitle?: string;
  phase?: string;
  error?: string;
  pendingDecision?: TicketsRunPendingDecision;
  reviewAutomation?: {
    status: 'idle' | 'queued' | 'running' | 'retrying' | 'blocked' | 'merged' | 'awaiting_external_review';
    message?: string;
    updatedAt?: number;
  };
  mergeReadiness?: TicketMergeReadinessDetails & {
    state:
      | 'local_review_failed'
      | 'awaiting_pr_creation'
      | 'awaiting_bugbot'
      | 'awaiting_checks'
      | 'awaiting_bugbot_and_checks'
      | 'remediation_in_progress'
      | 'ready_to_merge'
      | 'merged'
      | 'blocked_manual_review';
    bugbotApprovalState?: 'unknown' | 'awaiting' | 'approved' | 'blocking';
    requiredChecksState?: 'unknown' | 'pending' | 'passed' | 'failed';
  };
  latestEventSeq?: number;
  events?: TicketExecutionEvent[];
  totals: {
    total: number;
    completed: number;
    failed: number;
    manualRequired: number;
    skipped: number;
  };
  items: Array<{
    ticketId: string;
    ticketNumber?: number;
    title: string;
    repoRole?: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
    /** Skill id the router chose for the primary work unit. */
    skillId?: string;
    status: TicketsBuildRunItemStatus;
    failureCode?: string;
    reason?: string;
    warning?: string;
    owner?: string;
    repo?: string;
    pullRequestTitle?: string;
    pullRequestUrl?: string;
    proposedFileCount?: number;
    returnedToBacklog?: boolean;
    awaitingUserAction?: boolean;
    confirmationPrompt?: string;
    error?: string;
    workUnits?: TicketExecutionWorkUnit[];
    resumeDraft?: TicketExecutionResumeDraft;
    verificationPassed?: boolean;
    remediationAttempts?: number;
    latestCommands?: TicketExecutionCommandLog[];
    latestArtifacts?: TicketExecutionArtifact[];
    startedAt?: number;
    completedAt?: number;
    /** Per-repo planning-time skill confidence; distinct from mergeReadiness.confidence. */
    plannerRepoConfidence?: PlannerRepoConfidenceRow[];
  }>;
};

@Injectable({ providedIn: 'root' })
export class TicketsExecService {
  constructor(private api: MachineApiService) {}

  private async unwrap<T>(p: Promise<ApiResponse<T>>): Promise<T> {
    const res = await p;
    return res.data;
  }

  async executeTicket(input: TicketsExecuteInput): Promise<TicketsExecuteResult> {
    return this.unwrap(this.api.postJson<ApiResponse<TicketsExecuteResult>>('/api/tickets/execute', input));
  }

  async executeTicketAgent(input: TicketAgentExecutionInput): Promise<TicketsExecuteResult> {
    return this.unwrap(this.api.postJson<ApiResponse<TicketsExecuteResult>>('/api/tickets/execute/agent', input));
  }

  async startExecuteTicketAgentRun(input: TicketAgentExecutionInput): Promise<TicketsBuildRun> {
    return this.unwrap(this.api.postJson<ApiResponse<TicketsBuildRun>>('/api/tickets/execute/agent/run', input));
  }

  async preflight(input: TicketsPreflightInput): Promise<TicketsPreflightResult> {
    return this.unwrap(this.api.postJson<ApiResponse<TicketsPreflightResult>>('/api/tickets/preflight', input));
  }

  async planTicketExecution(input: TicketExecutionPlanInput): Promise<TicketExecutionPlanResult> {
    return this.unwrap(this.api.postJson<ApiResponse<TicketExecutionPlanResult>>('/api/tickets/plan', input));
  }

  async startBuildProject(input: {
    projectId: string;
    installationId?: number;
    repos: TicketRepoRef[];
    model?: string;
    projectContext?: TicketProjectContext;
  }): Promise<TicketsBuildRun> {
    return this.unwrap(this.api.postJson<ApiResponse<TicketsBuildRun>>('/api/tickets/build', input));
  }

  async getBuildRun(runId: string): Promise<TicketsBuildRun> {
    return this.unwrap(this.api.request<ApiResponse<TicketsBuildRun>>(`/api/tickets/build/${encodeURIComponent(runId)}`));
  }

  async getExecutionRun(runId: string): Promise<TicketsBuildRun> {
    return this.unwrap(this.api.request<ApiResponse<TicketsBuildRun>>(`/api/tickets/runs/${encodeURIComponent(runId)}`));
  }

  async cancelExecutionRun(runId: string): Promise<TicketsBuildRun> {
    return this.unwrap(this.api.postJson<ApiResponse<TicketsBuildRun>>(`/api/tickets/runs/${encodeURIComponent(runId)}/cancel`, {}));
  }

  async resolveExecutionRunDecision(runId: string, action: 'continue' | 'skip' | 'cancel'): Promise<TicketsBuildRun> {
    return this.unwrap(
      this.api.postJson<ApiResponse<TicketsBuildRun>>(`/api/tickets/runs/${encodeURIComponent(runId)}/decision`, { action })
    );
  }

  async resumeExecutionRun(runId: string, ticketId?: string): Promise<TicketsBuildRun> {
    return this.unwrap(
      this.api.postJson<ApiResponse<TicketsBuildRun>>(`/api/tickets/runs/${encodeURIComponent(runId)}/resume`, {
        ticketId: String(ticketId ?? '').trim() || undefined,
      })
    );
  }

  async followExecutionRunStream(runId: string, afterSeq?: number, signal?: AbortSignal): Promise<Response> {
    return this.api.streamPostJson(`/api/tickets/runs/${encodeURIComponent(runId)}/follow/stream`, {
      afterSeq: typeof afterSeq === 'number' ? afterSeq : undefined,
    }, { signal });
  }
}

